import { ref } from 'vue'
import { consola } from 'consola'

// Logger
const logger = consola.withTag('tabs-sync')

/**
 * Типы событий для синхронизации между вкладками
 */
export enum TabSyncEventType {
  // Токены
  TOKEN_REFRESHED = 'TOKEN_REFRESHED',
  TOKEN_REFRESH_STARTED = 'TOKEN_REFRESH_STARTED',
  TOKEN_REFRESH_FAILED = 'TOKEN_REFRESH_FAILED',
  
  // Авторизация
  USER_LOGGED_IN = 'USER_LOGGED_IN',
  USER_LOGGED_OUT = 'USER_LOGGED_OUT',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  
  // Сессия
  SESSION_UPDATED = 'SESSION_UPDATED',
  SESSION_CLEARED = 'SESSION_CLEARED',
}

/**
 * Интерфейс события синхронизации
 */
export interface TabSyncEvent {
  type: TabSyncEventType
  timestamp: number
  tabId: string
  data?: any
}

/**
 * Composable для синхронизации состояния между вкладками браузера
 * 
 * ✅ Использует BroadcastChannel API для коммуникации
 * ✅ Синхронизирует токены, авторизацию, разлогин
 * ✅ Предотвращает рассинхронизацию между вкладками
 * ✅ Автоматическая очистка при размонтировании
 * 
 * @example
 * const tabsSync = useTabsSync()
 * 
 * // Отправить событие
 * tabsSync.broadcast(TabSyncEventType.TOKEN_REFRESHED, { accessToken: '...' })
 * 
 * // Слушать события
 * tabsSync.on(TabSyncEventType.TOKEN_REFRESHED, (event) => {
 *   consola.debug('Токен обновлен в другой вкладке', event.data)
 * })
 */

// Глобальный BroadcastChannel (singleton)
let globalChannel: BroadcastChannel | null = null
let isInitialized = false

// Уникальный ID текущей вкладки
const currentTabId = ref<string>('')

// Обработчики событий
const eventHandlers = new Map<TabSyncEventType, Set<(event: TabSyncEvent) => void>>()

/**
 * Генерация уникального ID вкладки
 */
const generateTabId = (): string => {
  return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

/**
 * Инициализация BroadcastChannel
 */
const initializeChannel = (): void => {
  /* v8 ignore next */
  if (isInitialized || !process.client) return
  
  try {
    // Проверяем поддержку BroadcastChannel
    if (typeof BroadcastChannel === 'undefined') {
      logger.warn('BroadcastChannel не поддерживается в этом браузере')
      return
    }
    
    globalChannel = new BroadcastChannel('auth-sync-channel')
    currentTabId.value = generateTabId()
    isInitialized = true
    
    // Слушаем входящие сообщения
    globalChannel.addEventListener('message', (event: MessageEvent<TabSyncEvent>) => {
      const syncEvent = event.data
      
      // Игнорируем события от текущей вкладки
      if (syncEvent.tabId === currentTabId.value) {
        return
      }
      
      if (process.dev) {
        logger.debug(`📨 Получено событие: ${syncEvent.type}`, {
          fromTab: syncEvent.tabId,
          timestamp: new Date(syncEvent.timestamp).toISOString(),
          data: syncEvent.data,
        })
      }
      
      // Вызываем обработчики для этого типа события
      const handlers = eventHandlers.get(syncEvent.type)
      if (handlers) {
        for (const handler of handlers) {
          try {
            handler(syncEvent)
          } catch (error) {
            logger.error(`Ошибка в обработчике события ${syncEvent.type}:`, error)
          }
        }
      }
    })
    
    logger.success(`✅ BroadcastChannel инициализирован (tabId: ${currentTabId.value})`)
  } catch (error) {
    logger.error('Ошибка инициализации BroadcastChannel:', error)
  }
}

export const useTabsSync = () => {
  // Инициализируем при первом использовании
  if (!isInitialized && process.client) {
    initializeChannel()
  }
  
  /**
   * Отправить событие во все другие вкладки
   */
  const broadcast = (type: TabSyncEventType, data?: any): void => {
    if (!globalChannel) {
      if (process.dev) {
        logger.debug('BroadcastChannel не доступен, пропускаем broadcast')
      }
      return
    }
    
    const event: TabSyncEvent = {
      type,
      timestamp: Date.now(),
      tabId: currentTabId.value,
      data,
    }
    
    try {
      globalChannel.postMessage(event)
      
      if (process.dev) {
        logger.debug(`📤 Отправлено событие: ${type}`, {
          tabId: currentTabId.value,
          data,
        })
      }
    } catch (error) {
      logger.error('Ошибка отправки события:', error)
    }
  }
  
  /**
   * Подписаться на событие определенного типа
   */
  const on = (type: TabSyncEventType, handler: (event: TabSyncEvent) => void): void => {
    if (!eventHandlers.has(type)) {
      eventHandlers.set(type, new Set())
    }
    
    eventHandlers.get(type)!.add(handler)
    
    if (process.dev) {
      logger.debug(`👂 Добавлен обработчик для события: ${type}`)
    }
  }
  
  /**
   * Отписаться от события
   */
  const off = (type: TabSyncEventType, handler: (event: TabSyncEvent) => void): void => {
    const handlers = eventHandlers.get(type)
    if (handlers) {
      handlers.delete(handler)
      
      if (process.dev) {
        logger.debug(`👋 Удален обработчик для события: ${type}`)
      }
    }
  }
  
  /**
   * Закрыть BroadcastChannel и очистить обработчики
   */
  const close = (): void => {
    if (globalChannel) {
      globalChannel.close()
      globalChannel = null
      isInitialized = false
      eventHandlers.clear()
      logger.info('🛑 BroadcastChannel закрыт')
    }
  }
  
  /**
   * Проверить, инициализирован ли канал
   */
  const isChannelActive = (): boolean => {
    return isInitialized && !!globalChannel
  }
  
  return {
    // State
    currentTabId,
    isActive: isChannelActive,
    
    // Methods
    broadcast,
    on,
    off,
    close,
    
    // Enum для удобства
    EventType: TabSyncEventType,
  }
}

